import React, { useEffect, useMemo, useState } from "react";
import { Edit3, ShieldCheck, Star, BarChart3, ListFilter, X, Mail, GraduationCap, LogOut } from "lucide-react";
import "./ProfilePage.css";
import { profileApi, tracksApi } from "../../api/client";
import { getApiErrorMessage } from "../../api/http";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return "-";
  }
};

const cleanLeadNoise = (value) => {
  const text = `${value || ""}`.trim();
  if (!text) return "-";
  return text.replace(/^[?\s._\-:;|/\\]+/, "");
};

const ProfilePage = () => {
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [globalRank, setGlobalRank] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedFeedbackTask, setSelectedFeedbackTask] = useState(null);

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError("");
      try {
        const [{ data: profileData }, { data: leaderboardData }] = await Promise.all([
          profileApi.me(),
          tracksApi.globalLeaderboard(),
        ]);

        const nextProfile = profileData?.profile || null;
        setProfile(nextProfile);

        const userId = authUser?.id || authUser?._id;
        const rank = (leaderboardData?.leaderboard || []).find((entry) => String(entry.studentId) === String(userId));
        setGlobalRank(rank || null);
      } catch (err) {
        setError(getApiErrorMessage(err, "Failed to load profile"));
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [authUser?.id, authUser?._id]);

  const taskHistory = useMemo(() => {
    return (profile?.taskHistory || []).map((entry) => ({
      id: entry._id,
      date: formatDate(entry.submittedAt || entry.createdAt),
      name: entry.task?.title || "Untitled Task",
      course: entry.task?.track?.name || "Track",
      status: entry.status === "graded" ? "Graded" : entry.isLate ? "Late" : "Pending",
      grade: entry.status === "graded" ? `${entry.score}/${entry.task?.points || 0}` : "-",
      feedback: entry.feedback || "",
    }));
  }, [profile?.taskHistory]);

  const riskStatus = profile?.points >= 100 ? "Safe" : profile?.points >= 40 ? "Medium" : "High";
  const displayEmail = cleanLeadNoise(profile?.email || authUser?.email || "-");
  const displayFaculty = cleanLeadNoise(profile?.faculty || "Faculty");
  const displayUniversity = cleanLeadNoise(profile?.university || "University");

  const openFeedbackModal = (task) => {
    if (!task?.feedback) return;
    setSelectedFeedbackTask(task);
  };

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await logout();
      navigate("/", { replace: true });
    } finally {
      setLogoutLoading(false);
    }
  };

  return (
    <div className="profile-container bg-light-subtle min-vh-100 pb-5">
      <div className="container py-5">
        {loading ? <div className="alert alert-info py-2">Loading profile...</div> : null}
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}

        <div className="d-flex justify-content-between align-items-center mb-5 flex-wrap gap-3">
          <div className="d-flex align-items-center gap-4">
            <div className="profile-avatar-large shadow-sm"></div>
            <div>
              <h1 className="fw-bold mb-1">{profile?.fullName || authUser?.fullName || "Student"}</h1>
              <p className="text-muted mb-0 small d-flex align-items-center gap-2">
                <Mail size={14} />
                <span>{displayEmail}</span>
              </p>
              <p className="text-muted mb-0 small d-flex align-items-center gap-2">
                <GraduationCap size={14} />
                <span>{displayFaculty}, {displayUniversity}</span>
              </p>
            </div>
          </div>
          <div className="profile-header-actions">
            <button className="btn btn-primary-ieee rounded-pill px-4 py-2 d-flex align-items-center gap-2 shadow-sm" disabled>
              <Edit3 size={18} /> Edit Profile
            </button>
            <button
              type="button"
              className="btn profile-logout-btn rounded-pill px-4 py-2 d-flex align-items-center gap-2 shadow-sm"
              onClick={handleLogout}
              disabled={logoutLoading}
            >
              <LogOut size={18} />
              {logoutLoading ? "Logging out..." : "Logout"}
            </button>
          </div>
        </div>

        <div className="row g-4 mb-5">
          <div className="col-md-4">
            <div className="card metric-card border-0 shadow-sm p-4 h-100">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <p className="text-muted small fw-bold mb-1">Global Rank</p>
                  <h2 className="fw-bold display-6">#{globalRank?.rank || "-"}</h2>
                </div>
                <BarChart3 className="text-light-gray" size={32} />
              </div>
              <span className="badge-trend text-success small">Based on global leaderboard</span>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card metric-card border-0 shadow-sm p-4 h-100">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <p className="text-muted small fw-bold mb-1">Total Points</p>
                  <h2 className="fw-bold display-6">{profile?.points ?? authUser?.points ?? 0}</h2>
                </div>
                <Star className="text-light-gray" size={32} />
              </div>
              <p className="text-muted extra-small mb-0">Tracks joined: {(profile?.tracks || []).length}</p>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card metric-card border-0 shadow-sm p-4 h-100">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <p className="text-muted small fw-bold mb-1">Risk Status</p>
                  <span className="badge-safe py-1 px-3">{riskStatus}</span>
                </div>
                <ShieldCheck className="text-light-gray" size={32} />
              </div>
              <div className="risk-msg p-3 rounded-3 small text-muted">
                Stay consistent with your tasks and submissions to keep risk low.
              </div>
            </div>
          </div>
        </div>

        <div className="table-section mt-5">
          <div className="d-flex justify-content-between align-items-end mb-4 flex-wrap gap-2">
            <div>
              <h4 className="fw-bold mb-1">Task History</h4>
              <p className="text-muted small mb-0">View your submissions and grading feedback</p>
            </div>

            <div className="d-flex gap-2 align-items-center">
              <div>
                <select className="form-select rounded-pill border-0 shadow-sm py-2 ps-4 pe-5 small">
                  <option>All Semesters</option>
                </select>
              </div>
              <button className="btn btn-white shadow-sm rounded-circle p-2 border-0 btn-sort-custom" disabled>
                <ListFilter size={20} className="text-dark" />
              </button>
            </div>
          </div>

          <div className="card border-0 shadow-sm rounded-4 overflow-hidden profile-table-wrap">
            <table className="table align-middle mb-0 profile-table">
              <thead className="bg-light">
                <tr>
                  <th className="ps-4">DATE</th>
                  <th>ASSIGNMENT</th>
                  <th>STATUS</th>
                  <th>GRADE</th>
                  <th className="pe-4 text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {taskHistory.length ? (
                  taskHistory.map((task) => (
                    <tr key={task.id}>
                      <td className="ps-4 text-muted small">{task.date}</td>
                      <td>
                        <div className="fw-bold small">{task.name}</div>
                        <div className="text-muted extra-small">{task.course}</div>
                      </td>
                      <td>
                        <span className={`status-pill ${task.status.toLowerCase()}`}>{task.status}</span>
                      </td>
                      <td className="fw-bold small">{task.grade}</td>
                      <td className="pe-4 text-end">
                        <button
                          type="button"
                          className={`btn btn-link text-decoration-none small fw-bold profile-feedback-btn ${task.feedback ? "text-primary-ieee" : "text-muted"}`}
                          onClick={() => openFeedbackModal(task)}
                          disabled={!task.feedback}
                        >
                          {task.feedback ? "View Feedback" : "No Feedback"}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">
                      No task history found yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedFeedbackTask ? (
        <div className="profile-feedback-modal-backdrop" onClick={() => setSelectedFeedbackTask(null)}>
          <div className="profile-feedback-modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="profile-feedback-modal-head">
              <h5>Instructor Feedback</h5>
              <button
                type="button"
                className="profile-feedback-close-btn"
                onClick={() => setSelectedFeedbackTask(null)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="profile-feedback-meta">
              <span>{selectedFeedbackTask.name}</span>
              <span>{selectedFeedbackTask.date}</span>
              <span>{selectedFeedbackTask.grade}</span>
            </div>
            <p className="profile-feedback-body">{selectedFeedbackTask.feedback}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ProfilePage;
